import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, ArrowLeft, Sparkles, CheckCircle, ExternalLink } from 'lucide-react';
import { Button, Card, Modal } from '../components/UIComponents';
import SubscriptionCard from '../components/SubscriptionCard';
import UsageStats from '../components/UsageStats';
import InvoiceList from '../components/InvoiceList';
import { useSubscription, useQuota, useInvoices, usePlans } from '../hooks/useBilling';
import { useTranslation } from 'react-i18next';
import posthog from '../lib/posthog';

const Billing: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const { data: subscription, loading: subLoading, error: subError, cancel, openCustomerPortal, refetch: refetchSubscription } = useSubscription();
  const { data: quota, loading: quotaLoading, error: quotaError, refetch: refetchQuota } = useQuota();
  const { data: plans, loading: plansLoading } = usePlans();
  const {
    data: invoicesData,
    loading: invoicesLoading,
    currentPage,
    totalPages,
    nextPage,
    prevPage,
    hasNextPage,
    hasPrevPage,
  } = useInvoices();

  // Handle checkout success state from URL
  useEffect(() => {
    if (searchParams.get('checkout') === 'success') {
      setCheckoutSuccess(true);
      posthog.capture('checkout_completed');
      // Clean up URL
      searchParams.delete('checkout');
      setSearchParams(searchParams, { replace: true });
      // Refetch subscription and quota to get updated data
      refetchSubscription();
      refetchQuota();
    }
  }, [searchParams, setSearchParams, refetchSubscription, refetchQuota]);

  const loading = subLoading || quotaLoading;
  const error = subError || quotaError;

  // Check if user has a paid subscription (for showing Stripe management button)
  const hasPaidSubscription = subscription?.status === 'active' && subscription.plan_id !== 'free';

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await cancel(false);
      posthog.capture('subscription_cancelled', {
        plan_id: subscription?.plan_id,
      });
      setShowCancelConfirm(false);
    } catch {
      // Error is handled by the hook
    } finally {
      setCancelling(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      await openCustomerPortal();
      // openCustomerPortal will redirect, so this won't be reached
    } catch {
      // Error is handled by the hook
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="py-2">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600 dark:text-indigo-400" />
            <p className="mt-4 text-slate-600 dark:text-zinc-400">{t('billing.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-2">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <AlertCircle className="h-12 w-12 text-red-500 dark:text-red-400" />
            <p className="mt-4 text-slate-900 dark:text-white font-medium">{t('billing.errors.failedToLoad')}</p>
            <p className="mt-2 text-slate-600 dark:text-zinc-400">{error}</p>
            <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
              {t('common.tryAgain')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // No subscription - show upgrade prompt
  if (!subscription) {
    return (
      <div className="py-2">
        <div className="container mx-auto px-4 max-w-4xl">
         <button
           onClick={() => navigate(-1)}
           className="flex items-center gap-2 text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white mb-6"
         >
           <ArrowLeft className="h-4 w-4" />
           {t('common.back')}
         </button>

          <Card className="p-8 text-center">
            <Sparkles className="h-12 w-12 text-indigo-500 dark:text-indigo-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">{t('billing.noSubscription.title')}</h2>
            <p className="mt-2 text-slate-600 dark:text-zinc-400 max-w-md mx-auto">
              {t('billing.noSubscription.description')}
            </p>
            <Button
              variant="primary"
              className="mt-6"
              onClick={() => navigate('/pricing')}
            >
              {t('billing.viewPlans')}
            </Button>
          </Card>

          {quota && (
            <div className="mt-8">
              <UsageStats quota={quota} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="py-2">
      <div className="container mx-auto px-4 max-w-4xl">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </button>

        <h1 className="h-page mb-8">{t('billing.pageTitle')}</h1>

        {/* Checkout Success Message */}
        {checkoutSuccess && (
          <div className="mb-8 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-emerald-500 dark:text-emerald-400 shrink-0" />
            <div>
              <p className="font-medium text-emerald-800 dark:text-emerald-300">{t('billing.paymentSuccess.title')}</p>
              <p className="text-sm text-emerald-700 dark:text-emerald-400">{t('billing.paymentSuccess.message')}</p>
            </div>
          </div>
        )}

        {/* Subscription Card */}
        <div className="mb-8">
          <SubscriptionCard
            subscription={subscription}
            onUpgrade={() => navigate('/pricing')}
            onCancel={subscription.plan_id === 'free' ? undefined : () => setShowCancelConfirm(true)}
          />
        </div>

        {/* Usage Stats */}
        {quota && (
          <div className="mb-8">
            <UsageStats quota={quota} />
          </div>
        )}

        {/* Available Plans (for upgrade) */}
        {!plansLoading && plans && plans.length > 0 && (
          <div className="mb-8">
            <Card className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{t('billing.upgradePrompt.title')}</h3>
                  <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1">
                    {t('billing.upgradePrompt.description')}
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate('/pricing')}>
                    {t('billing.comparePlans')}
                  </Button>
                  {hasPaidSubscription && (
                    <Button
                      variant="outline"
                      onClick={handleManageSubscription}
                      disabled={portalLoading}
                    >
                      {portalLoading ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('common.loading')}
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <ExternalLink className="h-4 w-4" />
                          {t('billing.manageInStripe')}
                        </span>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Invoice History */}
        <div className="mb-8">
          <InvoiceList
            invoices={invoicesData?.invoices || []}
            total={invoicesData?.total || 0}
            currentPage={currentPage}
            totalPages={totalPages}
            onNextPage={nextPage}
            onPrevPage={prevPage}
            hasNextPage={hasNextPage}
            hasPrevPage={hasPrevPage}
            loading={invoicesLoading}
          />
        </div>

        {/* Cancel Confirmation Modal */}
        <Modal
          isOpen={showCancelConfirm}
          onClose={() => setShowCancelConfirm(false)}
          title={t('billing.cancel')}
          footer={
            <>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowCancelConfirm(false)}
                disabled={cancelling}
              >
                {t('billing.keepSubscription')}
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                onClick={handleCancel}
                isLoading={cancelling}
              >
                {t('billing.confirmCancel')}
              </Button>
            </>
          }
        >
          <p className="text-sm text-slate-600 dark:text-zinc-400 mb-4">
            {t('billing.cancelConfirmation')}
          </p>

          <div className="rounded-lg border border-slate-200 p-3 dark:border-zinc-700">
            <p className="font-medium text-slate-900 dark:text-zinc-100">{t('billing.cancelAtPeriodEnd')}</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-zinc-500">
              {t('billing.keepAccessUntil', { date: new Date(subscription.current_period_end).toLocaleDateString() })}
            </p>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default Billing;
